import React from 'react';
import PropTypes from 'prop-types';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import useMobileLayout from 'Hooks/useMobileLayout';
import Footer from 'Components/ClientApp/Desktop/Footer';
import { FDContentContainer, FDPrimaryActionButton, FDStack } from '@facet-wealth/design';
import { useWizardFinished } from 'Hooks/useRefreshEvent';
import PageHeader from 'Components/ComponentLibrary/Molecules/PageHeader';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { dataAttrs } from 'Helpers/Testing';
import KnowYourCustomerVerificationPicker
  from 'Views/Page/Content/Templates/Profile/Templates/ProfileTemplate/KnowYourCustomerVerificationPicker/KnowYourCustomerVerificationPicker';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import Section from './Section';

const useStyles = buildUseStyles('ProfileTemplate', (theme) => ({
  sections: {},
  footer: {
    position: 'static',
    transform: 'initial',
  },
  header: {
    margin: theme.spacing(0, -2),
  },
}));

const ProfileTemplate = ({
  resource,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const sections = resource.attr('sections').resources().list();
  const isMobile = useMobileLayout();
  const { refreshPageContentCollection, loadPageContentLink } = usePageContentContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  useBoneBackgroundColor();

  const onClickReferralPage = (relationship) => {
    logStandardizedEvent(categories.referralPage, eventTypes.clickButton, 'Click Invite a friend button in member profile');
    loadPageContentLink(relationship.link('related'));
  };

  // Refresh when we finish the estate planning request wizard
  useWizardFinished(() => {
    refreshPageContentCollection();
  }, [refreshPageContentCollection]);

  return (
    <>
      <FDContentContainer maxWidth="xl" {...dataAttrs('profile-page', resource)}>
        <PageHeader
          title={resource.attr('title')}
          subtitle={resource.attr('subtitle')}
          actions={
            <FDStack direction={'row'} spacing={2}>
              <KnowYourCustomerVerificationPicker resource={resource} />
              {resource.withRelationship(fr.relationships.referralPage.name,
                (rel) => (<FDPrimaryActionButton
                  isFullWidth={isMobile}
                  label={rel.meta('prompt')}
                  onClick={() => onClickReferralPage(rel)}
                />),
                () => null)}
            </FDStack>
          }
        />
        <Box classNames={classes.sections}>
          {sections.map(section => (
            <Section key={section.id()} sectionResource={section} />
          ))}
        </Box>
      </FDContentContainer>
      {!isMobile && <Footer className={classes.footer} />}
    </>
  );
};

ProfileTemplate.defaultProps = {
  className: '',
};

ProfileTemplate.propTypes = {
  className: PropTypes.string,
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default ProfileTemplate;
